/**
 * /verify/infra-decision/:id — the public, readable verify page for a signed strategy decision
 * (V105-5-5). PUBLIC, no sign-in. It reads the same public JSON a machine gets
 * (`GET /verify/infra-decision/:id?format=json`): the signed facts only — the decision id, when it
 * was decided, the signed content digest and whether the content and the signature check out. No
 * strategy, reason, names or workspace are shown (or served) here. The record checks out only when
 * BOTH the signature is valid and the content matches the signed digest (round-3 H1).
 * Layout: BoardroomMode, brand header, centred column.
 */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, BoardroomMode, Brand, Button, Card, Spinner } from '@aigovops/design-system';
import { fmtDate } from '../lib/format';
import '../components/programs/strategy-compare.css';
import '../components/programs/infra-inventory.css';

interface SignedFacts { decision_id: string; kind: string; decided_at: string | null; content_sha256: string | null; signature_valid: boolean; content_matches_digest: boolean }

async function fetchFacts(id: string): Promise<SignedFacts | null> {
  const r = await fetch(`/verify/infra-decision/${encodeURIComponent(id)}?format=json`, { headers: { accept: 'application/json' } });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error('unavailable');
  return (await r.json()) as SignedFacts;
}

/** The one result line: the decision date and whether the record checks out (signature AND content). */
export function verifyHeadline(f: SignedFacts): { ok: boolean; text: string } {
  const ok = f.signature_valid && f.content_matches_digest;
  return { ok, text: `${f.decided_at ? `Decided ${fmtDate(f.decided_at)} · ` : ''}${ok ? 'Signature checks out' : 'Does not check out'}` };
}

export function VerifyInfraDecision() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['verify-infra-decision', id], queryFn: () => fetchFacts(id), retry: false });
  const h = q.data ? verifyHeadline(q.data) : null;
  return (
    <BoardroomMode>
      <div className="gp-verify-page">
        <header className="gp-verify-head">
          <div className="gp-verify-inner">
            <Link to="/" className="gp-verify-brand"><Brand variant="glyph" /><Brand variant="wordmark" /></Link>
          </div>
        </header>
        <main className="gp-verify-inner gp-verify-main">
          <h1 className="gp-card-h">Signed strategy decision</h1>
          <Card>
            {q.isLoading ? <Spinner label="Checking the signature" /> : q.error ? (
              <Alert variant="error"><p>The signature can’t be checked right now.</p><Button compact onClick={() => void q.refetch()}>Try again</Button></Alert>
            ) : !q.data || !h ? (
              <Alert variant="warning"><p>No signed decision has this link. Check that the whole link was copied.</p></Alert>
            ) : (
              <>
                <p><b>{h.text}</b></p>
                {!h.ok ? <Alert variant="warning"><p>The record may have been changed after it was signed. Ask the sender for a new link.</p></Alert> : null}
                <details className="gp-cb-why">
                  <summary>Details</summary>
                  <dl className="gp-inv-src">
                    <div><dt>Decision</dt><dd>{q.data.decision_id}</dd></div>
                    <div><dt>Content digest (SHA-256)</dt><dd>{q.data.content_sha256 ?? 'none'}</dd></div>
                    <div><dt>Signature valid</dt><dd>{q.data.signature_valid ? 'Yes' : 'No'}</dd></div>
                    <div><dt>Content matches the digest</dt><dd>{q.data.content_matches_digest ? 'Yes' : 'No'}</dd></div>
                  </dl>
                </details>
              </>
            )}
          </Card>
        </main>
      </div>
    </BoardroomMode>
  );
}
